/**
 * Pure helpers that turn PrestaShop's free-form paint data into Marche / Tinte fields.
 * Used by `piano.ts` (the import plan) and covered by `tests/int/prestashop-normalizza.int.spec.ts`.
 *
 * Two sources, two formats:
 *
 * - catalogue products, one per paint: `FIAT - Vernice base opaca - 182/A ROSSO RIBES MET`,
 *   filed under a brand category (`categoria`). Mixed categories (MOTO, MINICAR) carry the
 *   brand inside the name instead: `Vernice base opaca - KAW VERDE LIGHT GREEN PEARL`.
 * - order lines of the generic product, where the customer picked Marca / Modello / Colore /
 *   Codice colore from a list: the brands are clean, they only need aliasing.
 */
import { famigliaDaNome } from '@/cercatinte/famiglie'
import type { Marche, Tinte } from '@/payload-types'

export type TipoMarca = Marche['tipo']
export type TipoTinta = NonNullable<Tinte['tipo']>

/** Uppercase, single spaces, trimmed. */
export const pulisci = (value: string | null | undefined): string =>
  (value ?? '').replace(/\s+/g, ' ').trim().toUpperCase()

/**
 * Matching key for a paint code: letters and digits only. `182/A`, `182 A` and `182-A` are
 * the same code typed three ways; `F.402` and `F402` too. A slash between two digits stays:
 * Piaggio's `8/11` (Azzurro Laguna) and `811` (Rosso Corallo) are two paints.
 */
export const chiaveCodice = (codice: string): string =>
  pulisci(codice)
    .replace(/(\d)\s*\/\s*(?=\d)/g, '$1~')
    .replace(/[^A-Z0-9~]/g, '')
    .replace(/~/g, '/')

/** Order-line brand spellings → canonical `marche.nome`. `null` = not a brand. */
const ALIAS_MARCA: Record<string, string | null> = {
  'FORD EUROPE': 'FORD',
  'FORD AMERICA': 'FORD',
  'FORD AUSTRALIA': 'FORD',
  'DACIA RENAULT': 'DACIA',
  'FAW VOLKSWAGEN': 'VOLKSWAGEN',
  'SHANGHAI VOLKSWAGEN': 'VOLKSWAGEN',
  'FAW MAZDA': 'MAZDA',
  'DONGFENG PEUGEOT': 'PEUGEOT',
  'GUANGZHOU HONDA': 'HONDA',
  'NANJING AUTOMOTIVE GROUP': 'NANJING FIAT',
  'GM AUSTRALIA': 'CHEVROLET',
  VAUXHALL: 'OPEL',
  'DS-AUTOMOBILES': 'DS',
  'BYD AUTO': 'BYD',
  'ROLLS ROYCE': 'ROLLS-ROYCE',
  'MOTO GUZZI': 'MOTO-GUZZI',
  GUZZI: 'MOTO-GUZZI',
  HARLEY: 'HARLEY-DAVIDSON',
  'HARLEY DAVIDSON': 'HARLEY-DAVIDSON',
  AGUSTA: 'MV AGUSTA',
  MICRO: 'MICROCAR',
  KAW: 'KAWASAKI',
  'SUZ.': 'SUZUKI',
  KEN: 'KENWORTH',
  RVI: 'RENAULT TRUCKS',
  'SETRA EVO': 'SETRA',
  KIMCO: 'KYMCO',
  'HON.M': 'HONDA',
  'LIG.': 'LIGIER',
  // Not brands: customers who typed the colour into the brand field.
  TINTA: null,
}

/** Canonical brand for an order-line or name-prefix spelling, `null` when it isn't a brand. */
export const marcaCanonica = (nome: string): string | null => {
  const pulito = pulisci(nome)
  if (!pulito) return null
  return pulito in ALIAS_MARCA ? ALIAS_MARCA[pulito] : pulito
}

const MOTO = new Set([
  'APRILIA',
  'BENELLI',
  'CAGIVA',
  'DUCATI',
  'GILERA',
  'HARLEY-DAVIDSON',
  'KAWASAKI',
  'KTM',
  'KYMCO',
  'LAMBRETTA',
  'MALAGUTI',
  'MBK',
  'MOTO-GUZZI',
  'MV AGUSTA',
  'PIAGGIO',
  'SYM',
  'TRIUMPH',
  'YAMAHA',
])
const INDUSTRIALE = new Set([
  'DAF',
  'FOTON',
  'FUSO',
  'IVECO',
  'KENWORTH',
  'MAN',
  'RENAULT TRUCKS',
  'SCANIA',
  'SETRA',
])

export const tipoMarca = (nome: string): TipoMarca =>
  MOTO.has(nome) ? 'moto' : INDUSTRIALE.has(nome) ? 'industriale' : 'auto'

/** Finish guessed from the colour name; `pastello` when nothing says otherwise. */
export const tipoTinta = (nome: string): TipoTinta => {
  const n = pulisci(nome)
  if (/\b(TRISTRATO|TRE STRATI|3 STRATI|TRICOAT)\b/.test(n)) return 'tre-strati'
  if (/PEARL|PERL|NACR|MICA/.test(n)) return 'perlata'
  if (/\bMET\b|\bMET\.|METALL/.test(n)) return 'metallizzata'
  if (/\b(OPACO|MATT|MAT)\b/.test(n)) return 'opaca'
  return 'pastello'
}

/**
 * Catalogue categories → brands. A string is the brand of every paint in it. An array lists the
 * brands the paint is filed under, for groups that share one code list: VAG (Volkswagen, Audi;
 * Seat and Skoda have their own categories) and Fiat (Lancia and Abarth have no category, their
 * paints are in FIAT). `'nel-nome'` means a mixed category whose names start (or, for Piaggio,
 * end) with the brand.
 */
export const CATEGORIE_MARCA: Record<string, string | string[] | 'nel-nome'> = {
  'ALFA ROMEO': 'ALFA ROMEO',
  BMW: 'BMW',
  'CHEVROLET DAEWOO': 'CHEVROLET',
  CHRYSLER: 'CHRYSLER',
  CITROEN: 'CITROEN',
  DACIA: 'DACIA',
  DAIHATSU: 'DAIHATSU',
  FERRARI: 'FERRARI',
  FIAT: ['FIAT', 'LANCIA', 'ABARTH'],
  FORD: 'FORD',
  GM: 'CHEVROLET',
  HONDA: 'HONDA',
  HYUNDAI: 'HYUNDAI',
  ISUZU: 'ISUZU',
  JAGUAR: 'JAGUAR',
  KIA: 'KIA',
  LADA: 'LADA',
  MASERATI: 'MASERATI',
  MAZDA: 'MAZDA',
  MERCEDES: 'MERCEDES',
  MINI: 'MINI',
  MINICAR: 'nel-nome',
  MITSUBISHI: 'MITSUBISHI',
  MOTO: 'nel-nome',
  NISSAN: 'NISSAN',
  OPEL: 'OPEL',
  PEUGEOT: 'PEUGEOT',
  PORSCHE: 'PORSCHE',
  RENAULT: 'RENAULT',
  ROVER: 'ROVER',
  SAAB: 'SAAB',
  SEAT: 'SEAT',
  SKODA: 'SKODA',
  SMART: 'SMART',
  SSANGYONG: 'SSANGYONG',
  SUBARU: 'SUBARU',
  SUZUKI: 'SUZUKI',
  TATA: 'TATA',
  TOYOTA: 'TOYOTA',
  'VAG GROUP VOLKSWAGEN': ['VOLKSWAGEN', 'AUDI'],
  VOLVO: 'VOLVO',
}

/** Every word of every brand we know, so "MICRO LIGIER 16RP1" doesn't read LIGIER as a code. */
const MARCHE_NOTE = new Set(
  [
    ...Object.keys(ALIAS_MARCA),
    ...Object.values(ALIAS_MARCA),
    ...Object.keys(CATEGORIE_MARCA),
    ...Object.values(CATEGORIE_MARCA).flat(),
    ...MOTO,
    ...INDUSTRIALE,
    'LIGIER',
    'AIXAM',
    'CHATENET',
    'BELLIER',
    'CASALINI',
    'GRECAV',
    'JDM',
    'LEAPMOTOR',
  ].flatMap((m) => (m ? m.replace(/\./g, '').split(/[\s-]+/) : [])),
)

/** Leading words that name the brand inside MOTO / MINICAR paints, longest first. */
const PREFISSI_NEL_NOME: [string[], string][] = [
  [['HARLEY', 'DAVIDSON'], 'HARLEY-DAVIDSON'],
  [['HARLEY', 'DAV.'], 'HARLEY-DAVIDSON'],
  [['HARLEY', 'DAV'], 'HARLEY-DAVIDSON'],
  [['HONDA', 'MOT.'], 'HONDA'],
  [['HONDA', 'MOT'], 'HONDA'],
  [['CAG.'], 'CAGIVA'],
  [['HONDA', 'MOTO'], 'HONDA'],
  [['BMW', 'MOTO'], 'BMW'],
  [['SUZ.', 'MOTO'], 'SUZUKI'],
  [['SUZUKI', 'MOTO'], 'SUZUKI'],
  [['MOTO', 'GUZZI'], 'MOTO-GUZZI'],
  // "MICRO LIGIER 16RP1 ROSSO": the minicar group, then the real brand.
  [['MICRO', 'LIGIER'], 'LIGIER'],
  [['MICRO', 'GRECAV'], 'GRECAV'],
  ...[
    'APRILIA',
    'BENELLI',
    'BMW',
    'CAGIVA',
    'DUCATI',
    'GILERA',
    'GUZZI',
    'HARLEY',
    'HON.M',
    'HONDA',
    'KAW',
    'KAWASAKI',
    'KEN',
    'KIMCO',
    'KTM',
    'KYMCO',
    'LAMBRETTA',
    'MALAGUTI',
    'PIAGGIO',
    'SUZ.',
    'SUZUKI',
    'TRIUMPH',
    'YAMAHA',
    'AIXAM',
    'MICRO',
    'MICROCAR',
    'LIGIER',
    'CHATENET',
    'BELLIER',
    'CASALINI',
    'GRECAV',
    'JDM',
    'LIG.',
    'LEAPMOTOR',
  ].map((p): [string[], string] => [[p], marcaCanonica(p) as string]),
]

/**
 * Letter-only words that are never codes: finish/qualifier words and short words. Colour words
 * are caught by `famigliaDaNome`, brand names are added below.
 */
const NON_CODICI = new Set([
  ...['SKY', 'ICE', 'SUN', 'SEA', 'OAK', 'ASH', 'TAN', 'NEW', 'BIG', 'JET', 'ART', 'VAR'],
  ...['DE', 'DI', 'DU', 'LA', 'LE', 'EL', 'IL', 'DEL', 'THE', 'AND', 'VON'],
  ...['MET', 'METALLIC', 'METALLIZZATO', 'METALLISE', 'PEARL', 'PERLA', 'MICA', 'SOLID'],
  ...['MATT', 'MAT', 'OPACO', 'FLAT', 'BASE', 'SPARKLE', 'SPARKLING', 'CRYSTAL', 'EFFECT'],
  ...['DARK', 'LIGHT', 'DEEP', 'MED', 'MEDIUM', 'BRT', 'BRIGHT', 'PALE', 'PURE', 'ROYAL'],
  ...['CHIARO', 'SCURO', 'FONCE', 'CLAIR', 'DUNKEL', 'HELL', 'SUPER', 'CUSTOM', 'GRAND'],
  ...['COLORE', 'TINTA', 'VERNICE', 'MOTO', 'MOT', 'AUTO', 'CAR'],
])

/** Letter-only codes are at most this long (Ford `CWFAWWA`, VAG `LHSC`, Renault `TERNY`). */
const MAX_CODICE_LETTERE = 7

/**
 * Short words that are real codes when they open a brand category's name: Hyundai `DE` (Alpine
 * Blue, confirmed by the orders), Peugeot `KAW` (Jaune Persepolis) / `KEN` (Gris Icare), Jaguar
 * `JDM` (Cobalt Blue). Elsewhere they are articles or brands. `DS` stays out: it opens Hyundai,
 * Peugeot and Mazda names alike, so it isn't a code.
 */
const PAROLE_CODICE_IN_TESTA = new Set(['DE', 'DU', 'LA', 'LE', 'KAW', 'KEN', 'JDM'])

const sembraCodice = (token: string, inizioLista: boolean, primoLibero = false): boolean => {
  const t = token.replace(/^\*+/, '')
  if (!t) return false
  if (/\d/.test(t)) return true
  // "U/U", "G.KAR.", "RJM-PJM" are codes; "MET." is a word with a full stop.
  const lettere = t.replace(/\.$/, '')
  if (/[/.-]/.test(lettere)) return /^[A-Z/.-]+$/.test(lettere) && !/^-|-$/.test(lettere)
  const libero = primoLibero && PAROLE_CODICE_IN_TESTA.has(lettere)
  // Letter-only codes (KTA, PCS, LHSC, X) only where a code list starts or continues.
  return (
    inizioLista &&
    /^[A-Z]+$/.test(lettere) &&
    lettere.length <= MAX_CODICE_LETTERE &&
    (libero || (!NON_CODICI.has(lettere) && !MARCHE_NOTE.has(lettere))) &&
    famigliaDaNome(lettere) === null
  )
}

/** A code read from the end of a name ("GRIGIO OPACO 209/B", "4/6"): digits and 3+ characters. */
const sembraCodiceInCoda = (token: string) =>
  /^[A-Z]{0,3}\.?\d[A-Z0-9./-]*$/.test(token) && token.length >= 3

/**
 * The code that names the paint, from a list read in order:
 * - a later `X/A` refines a bare `X` ("296 296/A BIANCO GELATO": 296 is Giallo Maya);
 * - a one-character code (Ford `3`, `D`, `F`) is reused across years, so a longer code after it
 *   (`3,XSC2790,PC8CWWA,W JUICE GREEN`) identifies the paint better.
 */
const ordinaCodici = (codici: string[]): string[] => {
  const [primo] = codici
  const chiave = chiaveCodice(primo)
  const migliore =
    codici.find((c) => new RegExp(`^${chiave}/[A-Z]{1,2}$`).test(pulisci(c).replace(/\s/g, ''))) ??
    (chiave.length === 1
      ? codici.find((c) => chiaveCodice(c).length >= 3 && /\d/.test(c))
      : undefined)
  return migliore ? [migliore, ...codici.filter((c) => c !== migliore)] : codici
}

/** "ROSSO FUOCO ROSSO FUOCO" → "ROSSO FUOCO". */
const togliRipetizione = (testo: string) => {
  const parole = testo.split(' ')
  const meta = parole.length / 2
  return Number.isInteger(meta) && parole.slice(0, meta).join(' ') === parole.slice(meta).join(' ')
    ? parole.slice(0, meta).join(' ')
    : testo
}

export type NomeAnalizzato = {
  codice: string
  codiciAlternativi: string[]
  nome: string
  nomiAlternativi: string[]
  /** Mixing reference after `//` (`S9-102-1`), kept for `noteInterne`. */
  nota?: string
}

/**
 * Splits `182/A ROSSO RIBES MET` / `40W,814S,GJM PEPPERDUST , MUSKAT GRAU` / `QH1 / SH1 MAGENTA`
 * into codes and names. Returns `null` when the text has no code (`GRIS GRIS SAFIR`).
 */
export const analizzaCodiceNome = (
  testo: string,
  {
    primoCodiceLibero = false,
  }: {
    /** The text comes from a brand category, so a short brand-like first word is a code. */
    primoCodiceLibero?: boolean
  } = {},
): NomeAnalizzato | null => {
  let resto = pulisci(testo)
  let nota: string | undefined
  const doppiaBarra = resto.indexOf('//')
  if (doppiaBarra >= 0) {
    nota = resto.slice(doppiaBarra + 2).trim() || undefined
    resto = resto.slice(0, doppiaBarra).trim()
  }

  // Put separators in their own tokens: "QH1 / SH1", "KXJ,PXJ", "572-3572" stays one token.
  const tokens = resto
    .replace(/\s*,\s*/g, ' , ')
    .replace(/\s+\/\s+/g, ' / ')
    .split(' ')
    .filter(Boolean)

  const codici: string[] = []
  let i = 0
  let attesoAltro = true
  while (i < tokens.length) {
    const t = tokens[i]
    if (t === ',' || t === '/') {
      // A separator continues the code list only when a code follows it.
      if (codici.length && i + 1 < tokens.length && sembraCodice(tokens[i + 1], true)) {
        attesoAltro = true
        i++
        continue
      }
      break
    }
    if (!attesoAltro && !/\d/.test(t)) break
    // A free first word still needs a name after it: "KIA - Vernice base opaca Kia" has no code.
    if (!sembraCodice(t, attesoAltro, primoCodiceLibero && i === 0 && tokens.length > 1)) break
    // Honda "NH 578", Chevrolet "WA 8555", Ford "XSC 1181.4", Piaggio "P. 910": a letter
    // prefix and its number.
    const prossimo = tokens[i + 1]
    // Fiat "182 A": a number and its letter suffix.
    const prefissoLettere =
      /^[A-Z]{1,3}\.?$/.test(t) && /^[A-Z]{0,2}\d[A-Z0-9/.-]*$/.test(prossimo ?? '')
    const suffissoLettera = /^\d+$/.test(t) && /^[A-Z]$/.test(prossimo ?? '')
    if (prefissoLettere || suffissoLettera) {
      codici.push(`${t} ${prossimo}`)
      attesoAltro = false
      i += 2
      continue
    }
    codici.push(t.replace(/^[*/]+/, ''))
    // Two codes in a row only when the next one has digits ("792 792A", "XSC6 1OA").
    attesoAltro = false
    i++
  }

  let tokensNome = tokens.slice(i)
  if (!codici.length) {
    // "GRIGIO OPACO 209/B", "DUCATI GRIGIO STEEL 291.601 291601": the code comes last.
    // Only without commas, where the end of the text is unambiguous.
    let fine = tokens.length
    while (fine > 0 && !tokens.includes(',') && sembraCodiceInCoda(tokens[fine - 1])) fine--
    if (fine === tokens.length || fine === 0) return null
    codici.push(...tokens.slice(fine))
    const inCoda = new Set(codici)
    tokensNome = [
      togliRipetizione(
        tokens
          .slice(0, fine)
          .filter((t) => !inCoda.has(t))
          .join(' '),
      ),
    ]
  }

  const nomi = tokensNome
    .join(' ')
    .split(/\s*,\s*/)
    .map((n) => n.replace(/^[-–/\s]+|[-–/\s]+$/g, '').trim())
    .filter(Boolean)

  // Slashes inside one token stay: "182/A" and Ford's "1/2R/1914C" are single codes.
  const [codice, ...altri] = ordinaCodici([...new Set(codici)])
  const [nome = '', ...nomiAlternativi] = [...new Set(nomi)]
  return {
    codice,
    codiciAlternativi: altri.filter((c) => chiaveCodice(c) !== chiaveCodice(codice)),
    nome,
    nomiAlternativi,
    ...(nota ? { nota } : {}),
  }
}

/** "YAMAHA WHITE PEARL MET" → "WHITE PEARL MET": order lines often repeat the brand. */
export const togliMarca = (marca: string, nome: string): string => {
  const pulito = pulisci(nome)
  for (const prefisso of [marca, marca.replace(/-/g, ' ')]) {
    if (pulito.startsWith(`${prefisso} `)) return pulito.slice(prefisso.length + 1).trim()
  }
  return pulito
}

export type TintaCatalogo = NomeAnalizzato & { marche: string[] }

/**
 * A catalogue product name + its default category → brand(s), codes and names.
 * `null` when the product isn't a paint, the category isn't a brand or no code can be read.
 */
export const analizzaProdottoCatalogo = (
  categoria: string,
  nomeProdotto: string,
): TintaCatalogo | null => {
  const regola = CATEGORIE_MARCA[pulisci(categoria)]
  if (!regola) return null

  // "Vernice base opaca - …", "Vernice base opaca …", "Vernice base opaca Lt. 25 - …"
  const pulito = pulisci(nomeProdotto)
  const match = /VERNICE BASE OPACA(?:\s+LT\.?\s*[\d.,]+)?\s*-?\s*/.exec(pulito)
  if (!match) return null
  let resto = pulito.slice(match.index + match[0].length)
  // "MOTO-Vernice base opaca - …": the category repeated in front of the name says nothing.
  let prima = pulito.slice(0, match.index).replace(/[\s-]+$/, '')
  if (prima === pulisci(categoria)) prima = ''

  let marche: string[]
  if (regola === 'nel-nome') {
    // "PIAGGIO Vernice base opaca - 760B GRIGIO COMETA": the brand can come first.
    if (prima) resto = `${prima} ${resto}`
    const tokens = resto.split(' ')
    const prefisso = PREFISSI_NEL_NOME.find(([parole]) => parole.every((p, k) => tokens[k] === p))
    if (prefisso) {
      marche = [prefisso[1]]
      resto = tokens.slice(prefisso[0].length).join(' ')
    } else if (/\bPIAGGIO\b/.test(resto)) {
      // "207 BLU PIAGGIO", "GR.POLA.ME GRIGIO POLARIS MET PIAGGIO 1.268.8200"
      marche = ['PIAGGIO']
      resto = resto.replace(/\s*\bPIAGGIO\b.*$/, '')
    } else {
      return null
    }
  } else {
    marche = Array.isArray(regola) ? regola : [regola]
    // "ALFA ROMEO 468 468/C VERDE MONZA VERNICE BASE OPACA": code and name before the words.
    if (!resto.trim() && prima.startsWith(`${marche[0]} `)) resto = prima
    // "CHEVROLET DAEWOO - Vernice base opaca Chevrolet 31U Denim Blue"
    for (const m of [...marche, pulisci(categoria)]) {
      if (resto.startsWith(`${m} `)) resto = resto.slice(m.length + 1)
    }
  }

  const analizzato = analizzaCodiceNome(resto, { primoCodiceLibero: regola !== 'nel-nome' })
  return analizzato ? { ...analizzato, marche } : null
}
